import React, { useEffect, useState, useCallback } from 'react';
import { object, string, ref } from 'yup';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import type { RegistrationFormData } from '@jwp/ott-common/types/account';
import { getModule } from '@jwp/ott-common/src/modules/container';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { checkConsentsFromValues, extractConsentValues, formatConsentsFromValues } from '@jwp/ott-common/src/utils/collection';
import useForm from '@jwp/ott-hooks-react/src/useForm';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import useQueryParam from '@jwp/ott-ui-react/src/hooks/useQueryParam';

import RegistrationForm from '../../../components/RegistrationForm/RegistrationForm';
import { useAriaAnnouncer } from '../../AnnouncementProvider/AnnoucementProvider';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';

const Registration = () => {
  const accountController = getModule(AccountController);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('account');
  const qr = useQueryParam('qr');
  const announce = useAriaAnnouncer();
  const [consentValues, setConsentValues] = useState<Record<string, string | boolean>>({});
  const [consentErrors, setConsentErrors] = useState<string[]>([]);
  const { isGoogleCaptchaEnabled, showOffersAfterSignUp, authentication_type, isSplitupFullname, isLastnameRequired } = useAppConfig();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const recaptchaAPI = useConfigStore((s) => s.appConfig.lambda.recaptcha_verification);

  const { publisherConsents, publisherConsentsLoading, loading } = useAccountStore(({ publisherConsents, publisherConsentsLoading, loading }) => ({
    publisherConsents,
    publisherConsentsLoading,
    loading,
  }));

  // const handleChangeConsent: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = ({ currentTarget }) => {
  //   if (!currentTarget) return;

  //   const { name, type } = currentTarget;
  //   const value = type === 'checkbox' ? (currentTarget as HTMLInputElement).checked : currentTarget.value;

  //   setConsentValues((current) => ({
  //     ...current,
  //     [name]: value,
  //   }));

  //   // Clear the errors for any checkbox that's toggled
  //   setConsentErrors((errors) => errors.filter((e) => e !== name));
  // };
  useEffect(() => {
    const raw = localStorage.getItem('jwapp.auth');
    const accessToken = localStorage.getItem('access_token');

    let shouldRedirect = false;

    if (raw) {
      try {
        const parsed = JSON.parse(raw);

        if (parsed?.accessToken && parsed?.refreshToken) {
          shouldRedirect = true;
        }
      } catch {
        // if JSON is invalid, do nothing
      }
    }

    if (accessToken) shouldRedirect = true;

    // Redirect if valid
    if (shouldRedirect) {
      navigate('/');
    }
  }, []);

  useEffect(() => {
    if (!publisherConsents) {
      accountController.getPublisherConsents();

      return;
    }

    setConsentValues(extractConsentValues(publisherConsents));
  }, [accountController, publisherConsents]);

  const nextStep = useCallback(() => {
    // const hasOffers = accessModel === ACCESS_MODEL.SVOD || (accessModel === ACCESS_MODEL.AUTHVOD && hasMediaOffers);
    navigate(modalURLFromLocation(location, showOffersAfterSignUp ? 'choose-offer' : 'welcome'), { replace: true });
  }, [navigate, location, showOffersAfterSignUp]);

  const { handleSubmit, handleChange, handleBlur, values, errors, validationSchemaError, submitting } = useForm<RegistrationFormData>({
    initialValues: { email: '', password: '', confirmPassword: '', firstName: '', lastName: '', fullName: '', termsAgreed: '' },

    // @ts-ignore - Yup dynamic optional strings struggle to align statically with RegistrationFormData non-optional string interfaces
    validationSchema: object().shape({
      email: string()
        .email(t('registration.field_is_not_valid_email'))
        .required(t('registration.field_required', { field: t('registration.email') })),
      password: string()
        .matches(/^(?=.*[a-z])(?=.*[0-9]).{8,}$/, t('registration.invalid_password', { field: t('registration.password') }))
        .required(t('registration.field_required', { field: t('registration.password') })),
      confirmPassword: string()
        .required(t('registration.field_required', { field: t('registration.confirm_password') }))
        .oneOf([ref('password')], t('registration.confirm_password_mismatch')),
      
      firstName: isSplitupFullname
        ? string()
            .min(2, t('registration.firstNameValidation'))
            .max(50, t('registration.firstNameValidation'))
            .required(t('registration.field_required', { field: t('registration.name') }))
        : string(), // Optional if not split
        
      lastName: isSplitupFullname
        ? string()
            .max(50, t('registration.lastNameValidation'))
            .test('is-required', t('registration.field_required', { field: t('personal_details.last_name') }), function (value) {
              if (isLastnameRequired && (!value || value.trim().length === 0)) {
                return false;
              }
              return true;
            })
        : string(),

      fullName: !isSplitupFullname
        ? string()
            .min(2, t('registration.fullNameValidation'))
            .max(100, t('registration.fullNameValidation'))
            .required(t('registration.field_required', { field: t('registration.name') }))
        : string(),

      termsAgreed: string().oneOf(['true'], t('registration.field_required')).required(t('registration.field_required')),
    }),
    validateOnBlur: true,
    onSubmit: async ({ email, password, firstName, lastName, fullName, termsAgreed }) => {
      if (isGoogleCaptchaEnabled) {
        if (!executeRecaptcha) {
          throw new Error('reCAPTCHA not ready');
        }

        const token = await executeRecaptcha('register');
        const verifyResponse = await fetch(recaptchaAPI, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        const { success, score, action } = await verifyResponse.json();

        if (!success || score < 0.5 || action !== 'register') {
          throw new Error('reCAPTCHA validation failed');
        }
      }

      const { consentsErrors } = checkConsentsFromValues(publisherConsents || [], consentValues);

      if (consentsErrors.length) {
        setConsentErrors(consentsErrors);
        throw new Error(t('registration.consents_error'));
      }

      let payloadFirstName = firstName;
      let payloadLastName = lastName;

      if (!isSplitupFullname) {
        payloadFirstName = fullName || '';
        payloadLastName = '';
      }

      await accountController.register(
        email,
        password,
        termsAgreed,
        payloadFirstName,
        payloadLastName,
        window.location.href,
        formatConsentsFromValues(publisherConsents, consentValues),
        t,
      );
      qr && navigate('/activate');
    },
    onSubmitSuccess: (values) => {
      const { email, firstName, lastName } = values;
      announce(t('registration.success'), 'success');
      sendGAEvent('sign_up', {
        email,
        method: authentication_type,
        first_name: firstName,
        last_name: lastName,
      });
      // navigate(modalURLFromLocation(location, 'personal-details'));
      !qr && nextStep();
    },
    onSubmitError: ({ error, resetValue }) => {
      resetValue('password');
      resetValue('confirmPassword');
      sendGAEvent('sign_up_error', {
        email: values.email,
        error_message: error,
      });
    },
  });

  return (
    <RegistrationForm
      onSubmit={handleSubmit}
      onChange={handleChange}
      onConsentChange={setConsentValues}
      onBlur={handleBlur}
      values={values}
      errors={errors}
      consentErrors={consentErrors}
      validationError={validationSchemaError}
      submitting={submitting}
      consentValues={consentValues}
      publisherConsents={publisherConsents}
      loading={loading || publisherConsentsLoading}
      isSplitupFullname={isSplitupFullname}
      isLastnameRequired={isLastnameRequired}
    />
  );
};

export default Registration;
